import { useEffect, useState } from 'react';
import './App.css';
import UnifiedAccessPortal from './UnifiedAccessPortal';
import HomePage from './HomePage';
import Navbar from './components/Navbar';
import AboutUsPage from './AboutUsPage';
import EducationProgramPage from './EducationProgramPage';
import AdmissionsPage from './AdmissionsPage';
import StudentLifePage from './StudentLifePage';
import HouseDetailPage from './HouseDetailPage';
import EventsCompetitionsPage from './EventsCompetitionsPage';
import FacilitiesPage from './FacilitiesPage';
import FacilityDetailPage from './FacilityDetailPage';
import TechnologyDetailPage from './TechnologyDetailPage';
import NewsUpdatesPage from './NewsUpdatesPage';
import ParentsPage from './ParentsPage';
import PaLCDetailPage from './PaLCDetailPage';
import CareersPage from './CareersPage';
import ContactUsPage from './ContactUsPage';
import RegisterPage from './RegisterPage';
import NewsDetailPage from './NewsDetailPage';
import NewsAllPage from './NewsAllPage';
import PreschoolPage from './PreschoolPage';
import KindergartenPage from './KindergartenPage';
import PrimaryPage from './PrimaryPage';
import SecondaryPage from './SecondaryPage';
import LoginGateway from './LoginGateway';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost/permataharapan-api/api';

function App() {
  // Handle hash-based routing for register page
  const getInitialSlug = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'register') {
      return 'register';
    }
    if (hash === 'login') {
      return 'login';
    }
    if (hash === 'parent-dashboard') {
      return 'parent-dashboard';
    }
    if (hash === 'staff-dashboard') {
      return 'staff-dashboard';
    }
    if (hash.startsWith('news/')) {
      return hash;
    }
    if (hash === 'news-all') {
      return 'news-all';
    }
    return 'homepage';
  };
  
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isAdministratorPath = normalizedPath === '/administrator';
  const isAdmissionsPath = normalizedPath === '/admissions';
  const isUnifiedGatePath = isAdministratorPath || isAdmissionsPath;

  const [menu, setMenu] = useState([]);
  const [language, setLanguage] = useState(() => {
    const saved = window.localStorage.getItem('phs-language');
    return saved === 'id' ? 'id' : 'en';
  });
  const [activeSlug, setActiveSlug] = useState(getInitialSlug());
  const [activeItemId, setActiveItemId] = useState(null);
  const [page, setPage] = useState(null);
  const [pageSource, setPageSource] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingPage, setLoadingPage] = useState(true);
  const [error, setError] = useState('');
  const [pendingAnchor, setPendingAnchor] = useState('');

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Handle hash changes for register page
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'register') {
        setActiveSlug('register');
        setActiveItemId(null);
      } else if (hash === 'login') {
        setActiveSlug('login');
        setActiveItemId(null);
      } else if (hash === 'parent-dashboard') {
        setActiveSlug('parent-dashboard');
        setActiveItemId(null);
      } else if (hash === 'staff-dashboard') {
        setActiveSlug('staff-dashboard');
        setActiveItemId(null);
      } else if (hash.startsWith('news/')) {
        setActiveSlug(hash);
        setActiveItemId(null);
      } else if (hash === 'news-all') {
        setActiveSlug('news-all');
        setActiveItemId(null);
      } else if (hash === '' && (activeSlug === 'register' || activeSlug === 'login' || activeSlug === 'parent-dashboard' || activeSlug === 'staff-dashboard' || activeSlug.startsWith('news/') || activeSlug === 'news-all')) {
        setActiveSlug('homepage');
        setActiveItemId(null);
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeSlug]);
  useEffect(() => {
    window.localStorage.setItem('phs-language', language);
    document.documentElement.lang = language === 'id' ? 'id' : 'en';
  }, [language]);
  useEffect(() => {
    async function loadMenu() {
      setLoadingMenu(true);
      try {
        // Use static menu data instead of fetching from API
        const staticMenu = [
          { id: 1, title: 'About Us', slug: 'about-us' },
          { id: 2, title: 'Education Program', slug: 'education-program' },
          { id: 3, title: 'Admissions', slug: 'admissions' },
          { id: 4, title: 'Student Life', slug: 'student-life' },
          { id: 5, title: 'Facilities', slug: 'facilities' },
          { id: 6, title: 'News and Updates', slug: 'news-and-updates' },
          { id: 7, title: 'Parents', slug: 'parents' },
          { id: 8, title: 'Careers', slug: 'careers' },
        ];
        setMenu(staticMenu);
        setError('');
      } catch (err) {
        console.error('Menu setup error:', err);
        setError('');
        setMenu([]);
      } finally {
        setLoadingMenu(false);
      }
    }

    if (isUnifiedGatePath) {
      setLoadingMenu(false);
      return;
    }

    loadMenu();
  }, [isUnifiedGatePath]);

  useEffect(() => {
    async function loadPage() {
      // Pages yang tidak perlu fetch dari API
      const staticPages = ['homepage', 'about-us', 'education-program', 'admissions', 'news-and-updates', 'facilities', 'student-life', 'parents', 'careers', 'contact-us'];
      
      if (!activeSlug || staticPages.includes(activeSlug)) {
        setLoadingPage(false);
        setPageSource(null);
        setPage(null);
        return;
      }

      setLoadingPage(true);
      try {
        const response = await fetch(`${API_BASE}/pages/${activeSlug}`);
        const payload = await response.json();
        if (!response.ok || !payload.ok) {
          throw new Error(payload.message || 'Failed to load page');
        }
        setPageSource(payload.data);
        setPage(payload.data);
      } catch (err) {
        setError(err.message || 'Page request failed');
      } finally {
        setLoadingPage(false);
      }
    }

    loadPage();
  }, [activeSlug]);

  useEffect(() => {
    async function translatePage() {
      if (!pageSource || activeSlug === 'homepage') {
        return;
      }
      if (language === 'en') {
        setPage(pageSource);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/translate-page`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'en',
            target: 'id',
            data: pageSource,
          }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.ok) {
          throw new Error(payload.message || 'Translate request failed');
        }
        setPage(payload.data);
      } catch (err) {
        setError(err.message || 'Translate request failed');
        setPage(pageSource);
      }
    }

    translatePage();
  }, [language, pageSource, activeSlug]);

  useEffect(() => {
    if (!pendingAnchor || loadingPage) {
      return;
    }

    const target = document.getElementById(pendingAnchor);
    const header = document.querySelector('.top-header');
    const headerOffset = header?.getBoundingClientRect().height || 0;
    const scrollToTarget = (node) => {
      const targetTop = node.getBoundingClientRect().top + window.pageYOffset;
      const offsetTop = Math.max(targetTop - headerOffset - 30, 0);
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    };
    if (target) {
      scrollToTarget(target);
      setPendingAnchor('');
      return;
    }

    const timeoutId = setTimeout(() => {
      const retryTarget = document.getElementById(pendingAnchor);
      if (retryTarget) {
        scrollToTarget(retryTarget);
      }
      setPendingAnchor('');
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [pendingAnchor, loadingPage]);

  useEffect(() => {
    if ((activeSlug === 'homepage' || activeSlug.startsWith('house-')) && !pendingAnchor) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeSlug, pendingAnchor]);

  useEffect(() => {
    if (!menu.length || activeSlug === 'homepage' || activeItemId !== null) {
      return;
    }

    for (const item of menu) {
      const itemKey = item.content_slug || item.slug;
      if (itemKey === activeSlug) {
        setActiveItemId(item.id);
        return;
      }

      if (item.children && item.children.length > 0) {
        const matchedChild = item.children.find(
          (child) => (child.content_slug || child.slug) === activeSlug
        );
        if (matchedChild) {
          setActiveItemId(matchedChild.id);
          return;
        }
      }
    }
  }, [menu, activeSlug, activeItemId]);

  function onSelectSlug(item) {
    setError('');
    setActiveSlug(item.content_slug || item.slug);
    setActiveItemId(item.id);
  }

  function goToSection(slug, anchor) {
    setError('');
    setActiveSlug(slug);
    setActiveItemId(null);
    setPendingAnchor(anchor);
  }

  function goToSlug(slug) {
    setError('');
    setActiveSlug(slug);
    setActiveItemId(null);
  }

  function goToHomepage() {
    setError('');
    setPage(null);
    setActiveSlug('homepage');
    setActiveItemId(null);
  }

  function findActiveItemTitle(items, targetId) {
    for (const item of items) {
      if (item.id === targetId) {
        return item.title;
      }
      if (item.children && item.children.length > 0) {
        const matchedChild = item.children.find((child) => child.id === targetId);
        if (matchedChild) {
          return matchedChild.title;
        }
      }
    }
    return null;
  }

  function findMenuItemByTitle(items, keyword) {
    const normalizedKeyword = keyword.toLowerCase();
    for (const item of items) {
      if (String(item.title || '').toLowerCase().includes(normalizedKeyword)) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const childMatch = item.children.find((child) =>
          String(child.title || '').toLowerCase().includes(normalizedKeyword)
        );
        if (childMatch) {
          return childMatch;
        }
      }
    }
    return null;
  }

  function getFallbackSubmenu(item) {
    const slug = item?.content_slug || item?.slug;
    const title = String(item?.title || '').toLowerCase();
    const fallbackMap = {
      'about-us': [
        
        { title: 'Welcome Message', slug: 'about-us', anchor: 'welcome-message' },
        { title: 'Vision, Mission & Values', slug: 'about-us', anchor: 'vision-mission-values' },
        { title: 'School Philosophy', slug: 'about-us', anchor: 'school-philosophy' },
        { title: 'History', slug: 'about-us', anchor: 'our-history' },
        { title: 'Leadership', slug: 'about-us', anchor: 'school-leadership' },
      ],
      'education-program': [
        
        { title: 'Programs', slug: 'education-program', anchor: 'program-levels' },
        { title: 'Curriculum Overview', slug: 'education-program', anchor: 'curriculum-overview' },
        { title: 'Subjects Offered', slug: 'education-program', anchor: 'subjects-offered' },
        { title: 'Learning Approach', slug: 'education-program', anchor: 'learning-approach' },
        { title: 'Assessment System', slug: 'education-program', anchor: 'assessment-system' },
        { title: 'Special Programs', slug: 'education-program', anchor: 'special-programs' },
        { title: 'ECA & Talent Programs', slug: 'education-program', anchor: 'eca-programs' },
      ],
      admissions: [
        
        { title: 'Admissions Process', slug: 'admissions', anchor: 'admissions-process' },
        { title: 'Requirements', slug: 'admissions', anchor: 'admissions-requirements' },
        { title: 'Key Dates', slug: 'admissions', anchor: 'admissions-key-dates' },
        { title: 'Fees and Payment', slug: 'admissions', anchor: 'fees-payment' },
        { title: 'Online Application', slug: 'admissions', anchor: 'online-application' },
      ],
      'student-life': [
        
        { title: 'Clubs & ECA', slug: 'student-life', anchor: 'clubs-eca' },
        { title: 'Events & Competitions', slug: 'student-life', anchor: 'events-competitions' },
        { title: 'House System', slug: 'student-life', anchor: 'house-system' },
        { title: 'Wellbeing & SEL', slug: 'student-life', anchor: 'wellbeing-sel' },
        { title: 'Community Service', slug: 'student-life', anchor: 'community-service' },
      ],
      
      facilities: [
        
        { title: 'Campus Overview', slug: 'facilities', anchor: 'campus-overview' },
        { title: 'Technology & Innovation', slug: 'facilities', anchor: 'technology-innovation' },
        { title: 'Safety & Security', slug: 'facilities', anchor: 'safety-security' },
        { title: 'Photo Gallery / Virtual Tour', slug: 'facilities', anchor: 'photo-gallery' },
      ],
      'news-and-updates': [
        { title: 'Announcements', slug: 'news-and-updates', anchor: 'announcements' },
        { title: 'Press Releases', slug: 'news-and-updates', anchor: 'press-releases' },
        { title: 'Blog / Articles', slug: 'news-and-updates', anchor: 'blog-articles' },
      ],
      parents: [
        { title: 'Parents Learning Community (PaLC)', slug: 'parents', anchor: 'parents-learning-community' },
        { title: 'Parent&Student Handbook', slug: 'parents', anchor: 'parent-student-handbook' },
        { title: 'Parent Portal Access', slug: 'parents', anchor: 'parent-portal-access' },
        { title: 'Communication Channels', slug: 'parents', anchor: 'communication-channels' },
      ],
      careers: [
        { title: 'Join Our Team', slug: 'careers', anchor: 'join-our-team' },
        { title: 'Open Positions', slug: 'careers', anchor: 'open-positions' },
        { title: 'Internships / Volunteering', slug: 'careers', anchor: 'internships-volunteering' },
      ],
      'contact-us': [
        { title: 'Contact Form', slug: 'contact-us', anchor: 'contact-form' },
        { title: 'Address and Map', slug: 'contact-us', anchor: 'contact-address' },
        { title: 'Phone, Email, and Office Hours', slug: 'contact-us', anchor: 'contact-address' },
        { title: 'Social Media Links', slug: 'contact-us', anchor: 'contact-address' },
        { title: 'FAQ', slug: 'contact-us', anchor: 'contact-faq' },
        { title: 'Partnerships / Affiliations', slug: 'contact-us', anchor: 'contact-partnerships' },
        { title: 'Testimonials', slug: 'contact-us', anchor: 'contact-testimonials' },
        { title: 'Construction Updates', slug: 'contact-us', anchor: 'contact-countdown' },
      ],
    };

    if (slug && fallbackMap[slug]) {
      return fallbackMap[slug];
    }

    if (slug === 'news-updates') {
      return fallbackMap['news-and-updates'];
    }

    if (slug === 'event-competitions') {
      return fallbackMap['events-competitions'];
    }

    if (slug === 'admissions' || title.includes('admission')) {
      return fallbackMap.admissions;
    }
    if (slug) {
      return [{ title: 'Overview', slug }];
    }
    return [];
  }

  function getSubmenuItemsForNavItem(item) {
    const itemSlug = item.content_slug || item.slug;
    const normalizedTitle = String(item.title || '').toLowerCase();
    const isAdmissionsMenu =
      itemSlug === 'admissions' || normalizedTitle.includes('admission');
    const isFacilitiesMenu =
      itemSlug === 'facilities' || normalizedTitle.includes('facilities');
    const isNewsUpdatesMenu =
      itemSlug === 'news-and-updates' ||
      itemSlug === 'news-updates' ||
      (normalizedTitle.includes('news') && normalizedTitle.includes('update'));
    const isParentsMenu = itemSlug === 'parents' || normalizedTitle.includes('parent');
    const isCareersMenu = itemSlug === 'careers' || normalizedTitle.includes('career');
    const fallbackSubmenu = getFallbackSubmenu(item);
    const preferFallback =
      isAdmissionsMenu ||
      isFacilitiesMenu ||
      isNewsUpdatesMenu ||
      isParentsMenu ||
      isCareersMenu ||
      [
        'about-us',
        'education-program',
        'student-life',
        'events-competitions',
        'event-competitions',
        'facilities',
        'news-and-updates',
        'parents',
        'careers',
        'contact-us',
      ].includes(itemSlug);

    if (preferFallback) {
      return fallbackSubmenu;
    }

    if (item.children && item.children.length > 0) {
      return item.children.map((child) => ({
        id: child.id,
        title: child.title,
        slug: child.content_slug || child.slug,
      }));
    }

    return fallbackSubmenu;
  }

  const isHomepage = activeSlug === 'homepage';
  const isAboutUs = activeSlug === 'about-us';
  const isEducationProgram = activeSlug === 'education-program';
  const isAdmissions = activeSlug === 'admissions';
  const isStudentLife = activeSlug === 'student-life';
  const isFacilities = activeSlug === 'facilities';
  const isFacilityDetail = activeSlug.startsWith('facility-');
  const isTechnologyDetail = activeSlug.startsWith('technology-');
  const activeItemTitle = activeItemId ? findActiveItemTitle(menu, activeItemId) : null;
  const isParents = activeSlug === 'parents';
  const isPaLCDetail = activeSlug.startsWith('palc-');
  const isCareers = activeSlug === 'careers';
  const isContactUs = activeSlug === 'contact-us';
  const isRegister = activeSlug === 'register';
  const isLogin = activeSlug === 'login';
  const isParentDashboard = activeSlug === 'parent-dashboard';
  const isStaffDashboard = activeSlug === 'staff-dashboard';
  const isNewsDetail = activeSlug.startsWith('news/');
  const isNewsPressDetail = activeSlug.startsWith('news-press-');
  const isNewsBlogDetail = activeSlug.startsWith('news-blog-');
  const isNewsAll = activeSlug === 'news-all';
  const isPreschool = activeSlug === 'preschool';
  const isKindergarten = activeSlug === 'kindergarten';
  const isPrimary = activeSlug === 'primary';
  const isSecondary = activeSlug === 'secondary';
  const isHouseDetail = activeSlug.startsWith('house-');
  const isNewsUpdatesByTitle =
    typeof activeItemTitle === 'string' &&
    activeItemTitle.toLowerCase().includes('news') &&
    activeItemTitle.toLowerCase().includes('update');
  const isNewsUpdates =
    activeSlug === 'news-and-updates' ||
    activeSlug === 'news-updates' ||
    isNewsUpdatesByTitle;
  const isEventsCompetitionsByTitle =
    typeof activeItemTitle === 'string' &&
    activeItemTitle.toLowerCase().includes('event') &&
    activeItemTitle.toLowerCase().includes('competition');
  const isEventsCompetitions =
    activeSlug === 'event-competitions' ||
    activeSlug === 'events-competitions' ||
    isEventsCompetitionsByTitle;
  const activeMainClass = isUnifiedGatePath
    ? 'main-content admin-main-content'
    : `main-content ${isHomepage || isAboutUs || isEducationProgram || isAdmissions || isStudentLife || isEventsCompetitions || isFacilities || isFacilityDetail || isTechnologyDetail || isNewsUpdates || isNewsPressDetail || isNewsBlogDetail || isParents || isPaLCDetail || isCareers || isContactUs || isRegister || isLogin || isParentDashboard || isStaffDashboard || isNewsDetail || isNewsAll || isPreschool || isKindergarten || isPrimary || isSecondary || isHouseDetail ? 'main-content-home' : ''}`;
  const campusLinks = ['Jakarta', 'Bekasi', 'Tangerang'].map((name) => ({
    name,
    item: findMenuItemByTitle(menu, name),
  }));
  const footerMenu = menu.length
    ? menu.filter(
        (item) =>
          item.slug !== 'homepage' &&
          item.slug !== 'careers' &&
          !String(item.title || '').toLowerCase().includes('career')
      )
    : [
        { title: 'About Us', slug: 'about-us' },
        { title: 'Education Program', slug: 'education-program' },
        { title: 'Admissions', slug: 'admissions' },
        { title: 'Student Life', slug: 'student-life' },
        { title: 'Facilities', slug: 'facilities' },
        { title: 'News and Updates', slug: 'news-and-updates' },
        { title: 'Parents', slug: 'parents' },
        { title: 'Contact Us', slug: 'contact-us' },
      ];
  const headerMenu = menu.length
    ? menu
    : [
        { title: 'About Us', slug: 'about-us' },
        { title: 'Education Program', slug: 'education-program' },
        { title: 'Admissions', slug: 'admissions' },
        { title: 'Student Life', slug: 'student-life' },
        { title: 'Facilities', slug: 'facilities' },
        { title: 'News and Updates', slug: 'news-and-updates' },
        { title: 'Parents', slug: 'parents' },
        { title: 'Careers', slug: 'careers' },
        { title: 'Contact Us', slug: 'contact-us' },
      ];

  return (
    <div className="app-shell">
      {!isUnifiedGatePath && !isRegister && !isLogin && !isParentDashboard && !isStaffDashboard && (
        <Navbar
          headerMenu={headerMenu}
          activeItemId={activeItemId}
          onSelectSlug={onSelectSlug}
          goToSlug={goToSlug}
          goToHomepage={goToHomepage}
          getSubmenuItemsForNavItem={getSubmenuItemsForNavItem}
          goToSection={goToSection}
          loadingMenu={loadingMenu}
        />
      )}

      <main className={activeMainClass}>
        {isUnifiedGatePath ? (
          <UnifiedAccessPortal apiBase={API_BASE} />
        ) : isHomepage ? (
          <HomePage language={language} onLanguageChange={setLanguage} onNavigate={goToSlug} />
        ) : isAboutUs ? (
          <div className="about-us-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <AboutUsPage page={page} />}
          </div>
        ) : isEducationProgram ? (
          <div className="education-program-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <EducationProgramPage page={page} onNavigate={goToSection} />}
          </div>
        ) : isAdmissions ? (
          <div className="admissions-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <AdmissionsPage page={page} />}
          </div>
        ) : isStudentLife ? (
          <div className="student-life-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <StudentLifePage page={page} onNavigate={goToSlug} />}
          </div>
        ) : isFacilities ? (
          <div className="facilities-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <FacilitiesPage page={page} onNavigate={goToSlug} />}
          </div>
        ) : isFacilityDetail ? (
          <div className="facility-detail-page-container">
            {(() => {
              const facilityId = activeSlug.replace('facility-', '');
              return <FacilityDetailPage facilityId={facilityId} onNavigate={goToSlug} />;
            })()}
          </div>
        ) : isTechnologyDetail ? (
          <div className="technology-detail-page-container">
            {(() => {
              const technologyId = activeSlug.replace('technology-', '');
              return <TechnologyDetailPage technologyId={technologyId} onNavigate={goToSlug} />;
            })()}
          </div>
        ) : isNewsUpdates ? (
          <div className="news-updates-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <NewsUpdatesPage page={page} onNavigate={goToSlug} />}
          </div>
        ) : isParents ? (
          <div className="parents-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <ParentsPage page={page} onNavigate={goToSlug} />}
          </div>
        ) : isPaLCDetail ? (
          <div className="palc-detail-page-container">
            {!loadingPage && <PaLCDetailPage palcId={activeSlug.replace('palc-', '')} onNavigate={goToSlug} />}
          </div>
        ) : isCareers ? (
          <div className="careers-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <CareersPage page={page} />}
          </div>
        ) : isContactUs ? (
          <div className="contact-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <ContactUsPage page={page} />}
          </div>
        ) : isRegister ? (
          <RegisterPage language={language} onNavigate={goToSlug} />
        ) : isLogin || isParentDashboard || isStaffDashboard ? (
          <LoginGateway apiBase={API_BASE} />
        ) : isNewsAll ? (
          <NewsAllPage language={language} />
        ) : isNewsDetail ? (
          <NewsDetailPage language={language} newsId={parseInt(activeSlug.split('/')[1])} />
        ) : isNewsPressDetail ? (
          <NewsDetailPage newsId={activeSlug.replace('news-press-', '')} onNavigate={goToSlug} />
        ) : isNewsBlogDetail ? (
          <NewsDetailPage newsId={activeSlug.replace('news-blog-', '')} onNavigate={goToSlug} />
        ) : isEventsCompetitions ? (
          <div className="events-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}
            {!loadingPage && <EventsCompetitionsPage page={page} />}
          </div>
        ) : isPreschool ? (
          <div className="preschool-page-container">
            <PreschoolPage onNavigate={goToSlug} />
          </div>
        ) : isKindergarten ? (
          <div className="kindergarten-page-container">
            <KindergartenPage onNavigate={goToSlug} />
          </div>
        ) : isPrimary ? (
          <div className="primary-page-container">
            <PrimaryPage onNavigate={goToSlug} />
          </div>
        ) : isSecondary ? (
          <div className="secondary-page-container">
            <SecondaryPage onNavigate={goToSlug} />
          </div>
        ) : isHouseDetail ? (
          <div className="house-detail-page-container">
            {(() => {
              return <HouseDetailPage houseName={activeSlug} onNavigate={goToSection} />;
            })()}
          </div>
        ) : (
          <div className="page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}

            {!loadingPage && page ? (
              <article className="page-article">
                <header className="article-header">
                  <h2>{page.title}</h2>
                  <p className="article-summary">{page.summary}</p>
                  <div className="article-meta">
                    <span className="badge">{page.status_code}</span>
                    <span className="meta-text">{page.updated_at}</span>
                  </div>
                </header>

                <section
                  className="article-body"
                  dangerouslySetInnerHTML={{ __html: page.body || '<p>No content body yet.</p>' }}
                />
              </article>
            ) : null}
          </div>
        )}
      </main>

      {!isUnifiedGatePath && !isRegister && !isLogin && !isParentDashboard && !isStaffDashboard && (
        <footer className="site-footer">
          <div className="site-footer-shell">
            <div className="site-footer-brand">
              <div className="site-footer-brand-lockup">
                <img src="/logo_phs.png" alt="PHS Logo" className="site-footer-logo" />
                <div>
                  <h3>Permata Harapan School</h3>
                  <p className="site-footer-tagline">School for Life</p>
                </div>
              </div>
              <p className="site-footer-address">
                Jl. Raya Permata Harapan No. 10, Jakarta Timur 13220
              </p>
              <div className="site-footer-contact">
                <a href="tel:+62215551234">Tel: +62 21 555 1234</a>
                <a href="https://wa.me/6281234567890" target="_blank" rel="noreferrer">
                  WhatsApp: +62 812 3456 7890
                </a>
              </div>
              <div className="site-footer-socials">
                <a
                  href="https://instagram.com/permataharapan"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  title="Instagram"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm5-2.25a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                  </svg>
                </a>
                <a
                  href="https://facebook.com/permataharapan"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                  title="Facebook"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M13 9h3V6h-3c-2.8 0-4.5 1.7-4.5 4.3V12H6v3h2.5v6H12v-6h3l.5-3H12v-1.5c0-1 0.4-1.5 1-1.5z" />
                  </svg>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube" title="YouTube">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M21.6 7.2a3 3 0 0 0-2.1-2.1C17.7 4.5 12 4.5 12 4.5s-5.7 0-7.5.6a3 3 0 0 0-2.1 2.1A31 31 0 0 0 2 12a31 31 0 0 0 .4 4.8 3 3 0 0 0 2.1 2.1c1.8.6 7.5.6 7.5.6s5.7 0 7.5-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 22 12a31 31 0 0 0-.4-4.8zM10 15.5v-7l6 3.5-6 3.5z" />
                  </svg>
                </a>
                <a href="https://tiktok.com" target="_blank" rel="noreferrer" aria-label="TikTok" title="TikTok">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M16 3c.3 2 1.7 3.7 3.6 4v2.5a7.1 7.1 0 0 1-3.6-1.2V14a5 5 0 1 1-5-5c.4 0 .8 0 1.2.1v2.6a2.5 2.5 0 1 0 1.8 2.4V3h2z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="site-footer-links">
              <h4>Menu Utama</h4>
              <ul>
                {footerMenu.map((item) => (
                  <li key={item.title}>
                    {item.id ? (
                      <button type="button" onClick={() => onSelectSlug(item)}>
                        {item.title}
                      </button>
                    ) : (
                      <button type="button" onClick={() => goToSlug(item.slug)}>
                        {item.title}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="site-footer-info">
              <h4>Informasi Sekolah</h4>
              <div className="site-footer-info-block">
                <p className="site-footer-info-title">Lokasi Sekolah</p>
                <div className="site-footer-campus">
                  {campusLinks.map((campus) => (
                    <button
                      key={campus.name}
                      type="button"
                      onClick={() =>
                        campus.item ? onSelectSlug(campus.item) : goToSlug('contact-us')
                      }
                    >
                      {campus.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="site-footer-info-block">
                <p className="site-footer-info-title">Terakreditasi oleh</p>
                <div className="site-footer-accreditation-logos">
                  <img
                    className="site-footer-accreditation-logo"
                    src="/cambridge-logo.webp"
                    alt="Cambridge International Education"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="site-footer-bottom">
            ©2026 Permata Harapan School. All Rights Reserved
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
